<template>
  <div class="container mt-4 mb-5 provisioning-page">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="m-0">Administration: Provisionierung</h3>
        <div class="text-muted small">
          Phase 1: manuelle Website-Übergabe, interne Freigabe, nur Shared-SaaS-Ausführung.
        </div>
      </div>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">Zurück</router-link>
    </div>

    <div v-if="!canRead" class="alert alert-warning">
      Keine Berechtigung zum Lesen der Provisionierungsanfragen.
    </div>

    <template v-else>
      <div class="mb-3 d-flex flex-wrap justify-content-center gap-1">
        <button v-if="canWrite" class="btn custom-btn provisioning-action" type="button" @click="openCreateModal">
          <i class="bi bi-file-earmark-plus"></i>
          <span>Neue Anfrage</span>
        </button>
        <button
          v-if="canWrite"
          class="btn custom-btn provisioning-action"
          type="button"
          :disabled="!canEditSelected"
          :title="editActionTitle"
          @click="openEditModal"
        >
          <i class="bi bi-pencil-square"></i>
          <span>Bearbeiten</span>
        </button>
        <button
          v-if="canApprove"
          class="btn custom-btn provisioning-action"
          type="button"
          :disabled="!canApproveSelected"
          :title="approveActionTitle"
          @click="approveRequest"
        >
          <i class="bi bi-check2-circle"></i>
          <span>Freigeben</span>
        </button>
        <button
          v-if="canExecute"
          class="btn custom-btn provisioning-action"
          type="button"
          :disabled="!canExecuteSelected"
          :title="executeActionTitle"
          @click="executeRequest"
        >
          <i class="bi bi-play-circle"></i>
          <span>Ausführen</span>
        </button>
        <button
          v-if="canExecute"
          class="btn custom-btn provisioning-action"
          type="button"
          :disabled="selected?.status !== 'failed'"
          :title="retryActionTitle"
          @click="retryRequest"
        >
          <i class="bi bi-arrow-repeat"></i>
          <span>Wiederholen</span>
        </button>
        <button
          v-if="canWrite && canApprove"
          class="btn custom-btn provisioning-action"
          type="button"
          :disabled="!canCancelSelected"
          :title="cancelActionTitle"
          @click="openCancelModal"
        >
          <i class="bi bi-slash-circle"></i>
          <span>Stornieren</span>
        </button>
        <button class="btn custom-btn provisioning-action" type="button" @click="loadRequests">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Neu laden</span>
        </button>
      </div>

      <div class="provisioning-workspace">
        <div class="provisioning-table-panel border border-2 rounded-3 shadow">
          <div class="row align-items-end py-4 px-3 g-2">
            <div class="col-md-3">
              <label class="form-label" for="provisioningStatusFilter">Status</label>
              <select id="provisioningStatusFilter" v-model="filter.status" class="form-select form-select-sm" @change="loadRequests">
                <option value="open_work">Offene Arbeit</option>
                <option value="all">Alle Anfragen</option>
                <optgroup label="Status">
                <option v-for="status in statuses" :key="status" :value="status">{{ labelStatus(status) }}</option>
                </optgroup>
              </select>
            </div>
            <div class="col-md-7">
              <label class="form-label" for="provisioningSearch">Suche</label>
              <div class="input-group input-group-sm">
                <input
                  id="provisioningSearch"
                  v-model="filter.search"
                  class="form-control"
                  type="search"
                  placeholder="Firma, Kontakt, E-Mail, Onboarding-Aufgabe"
                  @keyup.enter="loadRequests"
                />
                <button class="btn ehw-green" type="button" @click="loadRequests">
                  <i class="bi bi-search"></i>
                </button>
              </div>
            </div>
            <div class="col-md-2">
              <button class="btn btn-outline-secondary btn-sm w-100" type="button" @click="clearFilter">
                Filter löschen
              </button>
            </div>
          </div>

          <div class="table-responsive provisioning-table-wrapper">
            <table class="table table-bordered table-hover align-middle mb-0 provisioning-table">
              <thead class="thead-light">
                <tr>
                  <th style="min-width: 260px">Firma</th>
                  <th style="min-width: 220px">Kontakt</th>
                  <th style="min-width: 150px">Plan</th>
                  <th style="min-width: 190px">Status</th>
                  <th style="min-width: 170px">Onboarding</th>
                  <th style="min-width: 170px">Erstellt</th>
                </tr>
              </thead>
              <tbody v-if="requests.length > 0">
                <tr
                  v-for="request in requests"
                  :key="request.id"
                  :class="{ 'table-active': selectedRequestId === request.id }"
                  style="cursor: pointer"
                  @click="selectRequest(request.id)"
                  @dblclick="selectAndOpenEdit(request.id)"
                >
                  <td>
                    <div class="fw-semibold">{{ request.companyName }}</div>
                    <div class="text-muted small text-truncate" style="max-width: 300px">{{ request.notes || "-" }}</div>
                  </td>
                  <td>
                    <div>{{ request.contactName || "Keine Kontaktperson" }}</div>
                    <div class="text-muted small">{{ request.contactEmail }}</div>
                  </td>
                  <td>
                    <span class="badge me-1" :class="planBadgeClass(request.requestedPlanCode)">
                      {{ request.requestedPlanCode }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" :class="effectiveStatusBadgeClass(request)">
                      {{ effectiveStatusLabel(request) }}
                    </span>
                    <div v-if="hasOpenOnboardingTasks(request)" class="small text-warning-emphasis mt-1">
                      Provisionierung erledigt, Onboarding offen
                    </div>
                  </td>
                  <td>
                    <span class="badge" :class="onboardingBadgeClass(request)">
                      {{ onboardingSummary(request) }}
                    </span>
                  </td>
                  <td>{{ formatDate(request.creationTimestamp) }}</td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center text-muted" style="height: 160px; vertical-align: middle;">
                    Keine Anfragen gefunden.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="provisioning-mobile-list">
            <button
              v-for="request in requests"
              :key="`mobile-${request.id}`"
              class="provisioning-request-card"
              :class="{ active: selectedRequestId === request.id }"
              type="button"
              @click="selectRequest(request.id)"
            >
              <div class="d-flex justify-content-between align-items-start gap-2">
                <div class="min-w-0 text-start">
                  <div class="fw-semibold text-truncate">{{ request.companyName }}</div>
                  <div class="text-muted small text-truncate">{{ request.contactEmail }}</div>
                </div>
                <span class="badge flex-shrink-0" :class="effectiveStatusBadgeClass(request)">
                  {{ effectiveStatusLabel(request) }}
                </span>
              </div>

              <div class="d-flex flex-wrap gap-1 mt-2">
                <span class="badge" :class="planBadgeClass(request.requestedPlanCode)">
                  {{ request.requestedPlanCode }}
                </span>
                <span class="badge" :class="onboardingBadgeClass(request)">
                  {{ onboardingSummary(request) }}
                </span>
              </div>

              <div class="d-flex justify-content-between gap-2 mt-2 small text-muted">
                <span class="text-truncate">{{ request.contactName || "Keine Kontaktperson" }}</span>
                <span class="flex-shrink-0">{{ formatDate(request.creationTimestamp) }}</span>
              </div>
            </button>

            <div v-if="requests.length === 0" class="text-center text-muted py-4">
              Keine Anfragen gefunden.
            </div>
          </div>
        </div>

        <div ref="detailPanel" class="card provisioning-detail-panel">
          <div class="card-body">
          <div v-if="!selectedDetail" class="text-muted">
            Bitte eine Anfrage auswählen.
          </div>

          <template v-else>
            <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h5 class="card-title mb-1">{{ selected.companyName }}</h5>
                <div class="text-muted small">
                  {{ selected.contactName || "Keine Kontaktperson" }} · {{ selected.contactEmail }}
                </div>
              </div>
              <div class="text-end">
                <span class="badge me-1" :class="effectiveStatusBadgeClass(selected)">
                  {{ effectiveStatusLabel(selected) }}
                </span>
              </div>
            </div>

            <div v-if="selectedHasOpenOnboardingTasks" class="alert alert-warning d-flex gap-2 align-items-start">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <div>
                <div class="fw-semibold">Firma wurde provisioniert, aber das Kunden-Onboarding ist noch offen.</div>
                <div class="small">
                  {{ selected.openOnboardingTaskCount }} von {{ selected.onboardingTaskCount }} Aufgaben sind noch zu erledigen.
                </div>
              </div>
            </div>

            <div class="row g-3 mb-4">
              <div class="col-12">
                <div class="border rounded p-3 h-100">
                  <div class="fw-semibold mb-2">Anfragedaten</div>
                  <dl class="row mb-0 small">
                    <dt class="col-5">Plan</dt>
                    <dd class="col-7">{{ selected.requestedPlanCode }}</dd>
                    <dt class="col-5">Volumen</dt>
                    <dd class="col-7">{{ selected.expectedMonthlyVolume ?? "-" }}</dd>
                    <dt class="col-5">Overage-Cap</dt>
                    <dd class="col-7">{{ formatOverageCap(selected.maxOverageAmountCentsPerMonth) }}</dd>
                    <dt class="col-5">API</dt>
                    <dd class="col-7">{{ selected.apiAccessRequested ? "Ja" : "Nein" }}</dd>
                    <dt class="col-5">Mehrere Benutzer</dt>
                    <dd class="col-7">{{ selected.multipleUsersRequested ? "Ja" : "Nein" }}</dd>
                  </dl>
                </div>
              </div>
              <div class="col-12">
                <div class="border rounded p-3 h-100">
                  <div class="fw-semibold mb-2">Ausführung</div>
                  <dl class="row mb-0 small">
                    <dt class="col-5">Firma-ID</dt>
                    <dd class="col-7"><code>{{ selected.linkedCompanyId || "-" }}</code></dd>
                    <dt class="col-5">Freigegeben</dt>
                    <dd class="col-7">{{ formatDate(selected.approvedAt) }}</dd>
                    <dt class="col-5">Provisioniert</dt>
                    <dd class="col-7">{{ formatDate(selected.completedAt) }}</dd>
                    <dt class="col-5">Onboarding</dt>
                    <dd class="col-7">{{ onboardingSummary(selected) }}</dd>
                    <dt class="col-5">Fehler</dt>
                    <dd class="col-7 text-danger">{{ selected.failureMessage || "-" }}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">Onboarding-Aufgaben</h6>
                <span class="badge" :class="onboardingBadgeClass(selected)">
                  {{ onboardingSummary(selected) }}
                </span>
              </div>
              <div class="table-responsive">
                <table class="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Aufgabe</th>
                      <th>Status</th>
                      <th>Notiz</th>
                      <th class="text-end">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="task in onboardingTasks" :key="task.id" :class="{ 'table-warning': task.status === 'open' }">
                      <td>
                        <div class="fw-semibold">{{ task.title }}</div>
                        <div class="text-muted small">{{ task.description }}</div>
                      </td>
                      <td style="width: 160px">
                        <select v-model="task.status" class="form-select form-select-sm" :disabled="!canWriteOnboarding">
                          <option value="open">Offen</option>
                          <option value="completed">Erledigt</option>
                          <option value="cancelled">Storniert</option>
                        </select>
                      </td>
                      <td style="width: 220px">
                        <input v-model="task.notes" class="form-control form-control-sm" type="text" :disabled="!canWriteOnboarding" />
                      </td>
                      <td class="text-end">
                        <button
                          class="btn btn-outline-primary btn-sm"
                          type="button"
                          :disabled="!canWriteOnboarding"
                          @click="updateTask(task)"
                        >
                          Speichern
                        </button>
                      </td>
                    </tr>
                    <tr v-if="onboardingTasks.length === 0">
                      <td colspan="4" class="text-muted text-center py-3">
                        Aufgaben entstehen nach erfolgreicher Shared-SaaS-Ausführung.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="canReadAudit">
              <h6>Audit-Historie</h6>
              <div class="table-responsive">
                <table class="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Zeit</th>
                      <th>Ereignis</th>
                      <th>Status</th>
                      <th>Person</th>
                      <th>Nachricht</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="event in auditEvents" :key="event.id">
                      <td>{{ formatDate(event.creationTimestamp) }}</td>
                      <td><code>{{ event.eventType }}</code></td>
                      <td>{{ event.fromStatus || "-" }} → {{ event.toStatus || "-" }}</td>
                      <td>{{ event.actorDisplayName || "-" }}</td>
                      <td>{{ event.message || "-" }}</td>
                    </tr>
                    <tr v-if="auditEvents.length === 0">
                      <td colspan="5" class="text-muted text-center py-3">Keine Audit-Einträge.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
          </div>
        </div>
      </div>

      <div v-if="showCreateModal" class="modal fade show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-xl" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitCreateForm">
            <div class="modal-header">
              <div>
                <h5 class="modal-title">{{ isEditingRequest ? "Anfrage bearbeiten" : "Neue Anfrage erfassen" }}</h5>
                <div class="text-muted small">Website-Handoff bleibt manuell.</div>
              </div>
              <button type="button" class="btn-close" aria-label="Close" @click="closeCreateModal"></button>
            </div>
            <div class="modal-body">
              <div v-if="createFormError" class="alert alert-danger" role="alert">
                {{ createFormError }}
              </div>
              <div class="row g-3">
                <div class="col-md-4">
                  <label class="form-label" for="companyName">Firma *</label>
                  <input id="companyName" v-model="createForm.companyName" class="form-control" type="text" required />
                </div>
                <div class="col-md-4">
                  <label class="form-label" for="contactName">Kontaktperson</label>
                  <input id="contactName" v-model="createForm.contactName" class="form-control" type="text" />
                </div>
                <div class="col-md-4">
                  <label class="form-label" for="contactEmail">Kontakt-E-Mail *</label>
                  <input id="contactEmail" v-model="createForm.contactEmail" class="form-control" type="email" required />
                </div>
                <div class="col-md-3">
                  <label class="form-label" for="contactPhone">Telefon</label>
                  <input
                    id="contactPhone"
                    v-model="createForm.contactPhone"
                    class="form-control"
                    type="tel"
                    inputmode="tel"
                    autocomplete="tel"
                    pattern="(?=(?:\D*\d){3,15}\D*$)(?:\+|00)?[0-9\s.\(\)\/\-]+"
                    title="3 bis 15 Ziffern; ein führendes Plus sowie Leerzeichen, Klammern, Bindestriche, Schrägstriche und Punkte sind erlaubt."
                  />
                </div>
                <div class="col-md-3">
                  <label class="form-label" for="requestedPlan">Plan *</label>
                  <select id="requestedPlan" v-model="createForm.requestedPlanCode" class="form-select">
                    <option value="Flex">Flex</option>
                    <option value="Pro">Pro</option>
                    <option value="Business">Business</option>
                    <option value="Enterprise">Enterprise / Review</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label" for="expectedVolume">Erwartete Requests/Monat</label>
                  <input id="expectedVolume" v-model.number="createForm.expectedMonthlyVolume" class="form-control" type="number" min="0" />
                </div>
                <div class="col-md-3">
                  <label class="form-label" for="overageCap">Overage-Cap EUR/Monat</label>
                  <input id="overageCap" v-model.number="createForm.maxOverageAmountEurosPerMonth" class="form-control" type="number" min="0" step="0.01" />
                </div>
                <div class="col-12">
                  <div class="d-flex flex-wrap gap-3">
                    <label class="form-check">
                      <input v-model="createForm.apiAccessRequested" class="form-check-input" type="checkbox" />
                      <span class="form-check-label">API-Zugriff gewünscht</span>
                    </label>
                    <label class="form-check">
                      <input v-model="createForm.multipleUsersRequested" class="form-check-input" type="checkbox" />
                      <span class="form-check-label">Mehrere Benutzer</span>
                    </label>
                    <label class="form-check">
                      <input v-model="createForm.privateHostingRequested" class="form-check-input" type="checkbox" />
                      <span class="form-check-label">Private Hosting / Dedicated Review</span>
                    </label>
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label" for="notes">Notizen</label>
                  <textarea id="notes" v-model="createForm.notes" class="form-control" rows="2"></textarea>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" type="button" @click="closeCreateModal">Abbrechen</button>
              <button class="btn btn-primary" type="submit" :disabled="!canCreate">
                {{ isEditingRequest ? "Änderungen speichern" : "Anfrage erstellen" }}
              </button>
            </div>
            </form>
          </div>
        </div>
      </div>

      <div v-if="showReasonModal" class="modal fade show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ reasonForm.title }}</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="closeReasonModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-2">
                <div class="fw-semibold">{{ selected?.companyName }}</div>
                <div class="text-muted small">{{ selected?.contactEmail }}</div>
              </div>
              <label class="form-label" for="provisioningReason">{{ reasonForm.label }} *</label>
              <textarea
                id="provisioningReason"
                v-model="reasonForm.reason"
                class="form-control"
                rows="4"
                required
                @keyup.ctrl.enter="submitReasonModal"
              ></textarea>
              <div v-if="reasonForm.reason.length > 0 && !reasonForm.reason.trim()" class="text-danger small mt-1">
                Bitte einen Grund angeben.
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" type="button" @click="closeReasonModal">Abbrechen</button>
              <button class="btn btn-primary" type="button" :disabled="!canSubmitReason" @click="submitReasonModal">
                {{ reasonForm.submitText }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showCreateModal || showReasonModal" class="modal-backdrop fade show"></div>
    </template>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import ProvisioningService from "@/services/ProvisioningService";
import { useUserStore } from "@/stores/userStore";

const userStore = useUserStore();
const statuses = [
  "requested",
  "approved",
  "onboarding_open",
  "onboarding_done",
  "executing",
  "completed",
  "failed",
  "cancelled",
];

const requests = ref([]);
const selectedDetail = ref(null);
const selectedRequestId = ref(null);
const detailPanel = ref(null);
const showCreateModal = ref(false);
const editingRequestId = ref(null);
const showReasonModal = ref(false);
const filter = reactive({ status: "open_work", search: "" });
const createForm = reactive(newCreateForm());
const createFormError = ref("");
const reasonForm = reactive({
  action: "",
  title: "",
  label: "",
  submitText: "",
  reason: "",
});

const canRead = computed(() => userStore.hasPermission("provisioning.requests.read"));
const canWrite = computed(() => userStore.hasPermission("provisioning.requests.write"));
const canApprove = computed(() => userStore.hasPermission("provisioning.requests.approve"));
const canExecute = computed(() => userStore.hasPermission("provisioning.jobs.execute"));
const canWriteOnboarding = computed(() => userStore.hasPermission("provisioning.onboarding.write"));
const canReadAudit = computed(() => userStore.hasPermission("provisioning.audit.read"));
const selected = computed(() => selectedDetail.value?.request || null);
const isEditingRequest = computed(() => Boolean(editingRequestId.value));
const onboardingTasks = computed(() => selectedDetail.value?.onboardingTasks || []);
const auditEvents = computed(() => selectedDetail.value?.auditEvents || []);
const canCreate = computed(() => canWrite.value && createForm.companyName.trim() && createForm.contactEmail.trim());
const canEditSelected = computed(() =>
  canWrite.value &&
  selected.value &&
  !["executing", "completed", "failed", "cancelled"].includes(selected.value.status)
);
const canApproveSelected = computed(() => canApprove.value && selected.value?.status === "requested");
const canExecuteSelected = computed(() =>
  selected.value?.status === "approved" &&
  selected.value?.provisioningTarget === "shared_saas" &&
  selected.value?.requestedPlanCode !== "Enterprise"
);
const canCancelSelected = computed(() =>
  canWrite.value && canApprove.value &&
  selected.value &&
  !["completed", "executing", "cancelled"].includes(selected.value.status)
);
const selectedHasOpenOnboardingTasks = computed(() => hasOpenOnboardingTasks(selected.value));
const canSubmitReason = computed(() =>
  selected.value &&
  reasonForm.reason.trim().length > 0 &&
  reasonForm.action === "cancel" &&
  canCancelSelected.value
);
const editActionTitle = computed(() => canEditSelected.value ? "Anfrage bearbeiten" : editUnavailableReason());
const approveActionTitle = computed(() => canApproveSelected.value ? "Anfrage freigeben" : approveUnavailableReason());
const executeActionTitle = computed(() => canExecuteSelected.value ? "Shared-SaaS-Provisionierung ausführen" : executeUnavailableReason());
const retryActionTitle = computed(() => selected.value?.status === "failed" ? "Fehlgeschlagene Provisionierung wiederholen" : retryUnavailableReason());

const cancelActionTitle = computed(() => canCancelSelected.value ? "Anfrage stornieren" : unavailableActionReason("Stornieren"));

function newCreateForm() {
  return {
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    requestedPlanCode: "Flex",
    expectedMonthlyVolume: null,
    apiAccessRequested: false,
    multipleUsersRequested: false,
    privateHostingRequested: false,
    maxOverageAmountEurosPerMonth: null,
    notes: "",
  };
}

function resetCreateForm() {
  Object.assign(createForm, newCreateForm());
}

function populateCreateForm(request) {
  Object.assign(createForm, {
    companyName: request?.companyName || "",
    contactName: request?.contactName || "",
    contactEmail: request?.contactEmail || "",
    contactPhone: request?.contactPhone || "",
    requestedPlanCode: request?.requestedPlanCode || "Flex",
    expectedMonthlyVolume: request?.expectedMonthlyVolume ?? null,
    apiAccessRequested: Boolean(request?.apiAccessRequested),
    multipleUsersRequested: Boolean(request?.multipleUsersRequested),
    privateHostingRequested: Boolean(request?.privateHostingRequested),
    maxOverageAmountEurosPerMonth: centsToEuros(request?.maxOverageAmountCentsPerMonth),
    notes: request?.notes || "",
  });
}
function openCreateModal() {
  editingRequestId.value = null;
  createFormError.value = "";
  resetCreateForm();
  showCreateModal.value = true;
}

function closeCreateModal() {
  showCreateModal.value = false;
  editingRequestId.value = null;
  createFormError.value = "";
}

function openEditModal() {
  if (!canEditSelected.value) return;
  editingRequestId.value = selected.value.id;
  createFormError.value = "";
  populateCreateForm(selected.value);
  showCreateModal.value = true;
}
function openCancelModal() {
  if (!canCancelSelected.value) return;
  Object.assign(reasonForm, {
    action: "cancel",
    title: "Anfrage stornieren",
    label: "Stornierungsgrund",
    submitText: "Stornieren",
    reason: "",
  });
  showReasonModal.value = true;
}

function closeReasonModal() {
  showReasonModal.value = false;
  reasonForm.reason = "";
}

function toNullableNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function centsToEuros(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / 100 : null;
}

function eurosToCents(value) {
  const euros = toNullableNumber(value);
  return euros === null ? null : Math.round(euros * 100);
}

function createPayloadFromForm() {
  return {
    companyName: createForm.companyName,
    contactName: createForm.contactName || null,
    contactEmail: createForm.contactEmail,
    contactPhone: createForm.contactPhone || null,
    requestedPlanCode: createForm.requestedPlanCode,
    expectedMonthlyVolume: toNullableNumber(createForm.expectedMonthlyVolume),
    apiAccessRequested: Boolean(createForm.apiAccessRequested),
    multipleUsersRequested: Boolean(createForm.multipleUsersRequested),
    privateHostingRequested: Boolean(createForm.privateHostingRequested),
    maxOverageAmountCentsPerMonth: eurosToCents(createForm.maxOverageAmountEurosPerMonth),
    notes: createForm.notes || null,
  };
}

function applyDetail(detail) {
  selectedDetail.value = detail || null;
  selectedRequestId.value = detail?.request?.id || null;
}

function clearSelection() {
  selectedDetail.value = null;
  selectedRequestId.value = null;
}

async function loadRequests({ reconcileSelection = true } = {}) {
  if (!canRead.value) {
    requests.value = [];
    clearSelection();
    return;
  }

  const status = filter.status === "open_work" || filter.status === "all"
    ? null
    : filter.status;
  const response = await ProvisioningService.getRequests({
    status,
    search: filter.search || null,
    hideClosed: filter.status === "open_work",
    page: 1,
    pageSize: 100,
  });
  requests.value = Array.isArray(response?.data) ? response.data : [];
  if (reconcileSelection) {
    await reconcileSelectedRequest();
  }
}

async function reconcileSelectedRequest() {
  if (!selectedRequestId.value) return;
  const selectedIsVisible = requests.value.some((request) => request.id === selectedRequestId.value);
  if (!selectedIsVisible) {
    clearSelection();
    return;
  }

  const response = await ProvisioningService.getRequest(selectedRequestId.value);
  applyDetail(response?.data || null);
}

async function selectRequest(id) {
  const response = await ProvisioningService.getRequest(id);
  applyDetail(response?.data || null);
  await scrollDetailIntoViewOnSmallScreens();
}

async function selectAndOpenEdit(id) {
  await selectRequest(id);
  openEditModal();
}

async function submitCreateForm() {
  createFormError.value = "";

  try {
    if (isEditingRequest.value) {
      await updateRequest();
      return;
    }

    await createRequest();
  } catch (error) {
    createFormError.value = provisioningSaveErrorMessage(error);
  }
}
function provisioningSaveErrorMessage(error) {
  const data = error?.response?.data;
  const message = data?.detail || data?.title || data?.message;
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  if (error?.response?.status === 409) {
    return "Diese Firma existiert bereits und hat zugeordnete Benutzer. Bitte keine neue Provisionierung fuer dieselbe Firma anlegen.";
  }

  return "Die Provisionierungsanfrage konnte nicht gespeichert werden.";
}

async function createRequest() {
  if (!canCreate.value) return;
  const response = await ProvisioningService.createRequest(createPayloadFromForm());
  applyDetail(response?.data || null);
  resetCreateForm();
  closeCreateModal();
  await loadRequests();
}

async function updateRequest() {
  if (!canCreate.value || !editingRequestId.value) return;
  const response = await ProvisioningService.updateRequest(editingRequestId.value, createPayloadFromForm());
  applyDetail(response?.data || null);
  resetCreateForm();
  closeCreateModal();
  await loadRequests();
}
async function approveRequest() {
  if (!selected.value || !canApproveSelected.value) return;
  const notes = window.prompt("Freigabe-Notiz (optional):", selected.value.approvalNotes || "") || null;
  const response = await ProvisioningService.approveRequest(selected.value.id, notes);
  applyDetail(response?.data || null);
  await loadRequests();
}


async function cancelRequest() {
  if (!canCancelSelected.value || !canSubmitReason.value) return;
  const response = await ProvisioningService.cancelRequest(selected.value.id, reasonForm.reason.trim());
  applyDetail(response?.data || null);
  closeReasonModal();
  await loadRequests();
}

async function submitReasonModal() {
  if (!canSubmitReason.value) return;

  if (reasonForm.action === "cancel") {
    await cancelRequest();
  }
}

async function executeRequest() {
  if (!selected.value || !canExecuteSelected.value) return;
  const confirmed = window.confirm("Shared-SaaS-Provisionierung jetzt ausführen?");
  if (!confirmed) return;
  const response = await ProvisioningService.executeSharedSaas(selected.value.id);
  applyDetail(response?.data || null);
  await loadRequests();
}

async function retryRequest() {
  if (!selected.value || selected.value.status !== "failed") return;
  const response = await ProvisioningService.retry(selected.value.id);
  applyDetail(response?.data || null);
  await loadRequests();
}

async function updateTask(task) {
  if (!canWriteOnboarding.value) return;
  await ProvisioningService.updateOnboardingTask(task.id, {
    status: task.status,
    notes: task.notes || null,
  });
  await selectRequest(selected.value.id);
  await loadRequests();
}

async function scrollDetailIntoViewOnSmallScreens() {
  if (typeof window === "undefined" || !window.matchMedia("(max-width: 991.98px)").matches) {
    return;
  }

  await nextTick();
  detailPanel.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function unavailableActionReason(action) {
  if (!selected.value) return "Bitte zuerst eine Anfrage auswählen.";
  return `${action} ist im Status "${labelStatus(selected.value.status)}" nicht möglich.`;
}

function editUnavailableReason() {
  if (!selected.value) return "Bitte zuerst eine Anfrage auswählen.";
  return `Bearbeiten ist im Status "${labelStatus(selected.value.status)}" nicht möglich.`;
}

function approveUnavailableReason() {
  if (!selected.value) return "Bitte zuerst eine Anfrage auswählen.";
  return "Freigabe ist nur für angefragte Anfragen möglich.";
}

function executeUnavailableReason() {
  if (!selected.value) return "Bitte zuerst eine Anfrage auswählen.";
  if (selected.value.status !== "approved") return "Ausführung ist nur im Status Freigegeben möglich.";
  if (selected.value.provisioningTarget !== "shared_saas") return "Ausführung ist nur für Shared-SaaS-Anfragen möglich.";
  if (selected.value.requestedPlanCode === "Enterprise") return "Enterprise-Anfragen sind in Phase 1 nur Review-Anfragen.";
  return "Ausführung ist für diese Anfrage nicht möglich.";
}

function retryUnavailableReason() {
  if (!selected.value) return "Bitte zuerst eine Anfrage auswählen.";
  return "Wiederholen ist nur bei fehlgeschlagenen Provisionierungen möglich.";
}

function clearFilter() {
  filter.status = "open_work";
  filter.search = "";
  loadRequests();
}

function labelStatus(status) {
  const map = {
    requested: "Angefragt",
    approved: "Freigegeben",
    onboarding_open: "Onboarding offen",
    onboarding_done: "Onboarding erledigt",
    executing: "In Ausführung",
    completed: "Provisioniert",
    failed: "Fehlgeschlagen",
    cancelled: "Storniert",
  };
  return map[status] || status || "-";
}

function onboardingTaskCount(request) {
  if (!request) return 0;
  if (Number.isFinite(request.onboardingTaskCount)) return request.onboardingTaskCount;
  return request.id === selected.value?.id ? onboardingTasks.value.length : 0;
}

function openOnboardingTaskCount(request) {
  if (!request) return 0;
  if (Number.isFinite(request.openOnboardingTaskCount)) return request.openOnboardingTaskCount;
  return request.id === selected.value?.id
    ? onboardingTasks.value.filter((task) => task.status === "open").length
    : 0;
}

function hasOpenOnboardingTasks(request) {
  return request?.status === "completed" && openOnboardingTaskCount(request) > 0;
}

function effectiveStatusLabel(request) {
  if (hasOpenOnboardingTasks(request)) return "Onboarding offen";
  if (request?.status === "completed" && onboardingTaskCount(request) > 0) return "Onboarding erledigt";
  return labelStatus(request?.status);
}

function effectiveStatusBadgeClass(request) {
  return hasOpenOnboardingTasks(request) ? "text-bg-warning" : statusBadgeClass(request?.status);
}

function onboardingSummary(request) {
  const total = onboardingTaskCount(request);
  const open = openOnboardingTaskCount(request);

  if (total === 0) {
    return request?.status === "completed" ? "Keine Aufgaben" : "Nach Provisionierung";
  }

  return open > 0 ? `${open} offen / ${total} gesamt` : "Alle erledigt";
}

function onboardingBadgeClass(request) {
  if (openOnboardingTaskCount(request) > 0) return "text-bg-warning";
  if (onboardingTaskCount(request) > 0) return "text-bg-success";
  return "text-bg-light";
}

function statusBadgeClass(status) {
  return {
    requested: "text-bg-secondary",
    approved: "text-bg-primary",
    executing: "text-bg-warning",
    completed: "text-bg-success",
    failed: "text-bg-danger",
    cancelled: "text-bg-dark",
  }[status] || "text-bg-secondary";
}

function planBadgeClass(plan) {
  return String(plan).toLowerCase() === "enterprise" ? "text-bg-dark" : "text-bg-light";
}


function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("de-DE");
  } catch (_) {
    return value;
  }
}

function formatOverageCap(value) {
  const euros = centsToEuros(value);
  if (euros === null) return "Kein Cap";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(euros);
}

onMounted(async () => {
  if (!userStore.me) {
    await userStore.loadMe();
  }

  await loadRequests();
});
</script>

<style scoped>
.provisioning-action {
  align-items: center;
  display: inline-flex;
  flex-direction: column;
  gap: 0.25rem;
  min-height: 82px;
  min-width: 125px;
}

.provisioning-action i {
  font-size: 2rem;
  line-height: 1;
}

.provisioning-page {
  max-width: none;
  width: min(100% - 1.5rem, 2200px);
}

.provisioning-workspace {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(760px, 1fr) minmax(560px, 620px);
}

.provisioning-table-panel {
  min-width: 0;
  overflow: hidden;
}

.provisioning-table {
  min-width: 1120px;
}

.provisioning-mobile-list {
  display: none;
}

.provisioning-detail-panel {
  max-height: calc(100vh - 1.5rem);
  overflow: auto;
  position: sticky;
  top: 0.75rem;
}

@media (max-width: 991.98px) {
  .provisioning-workspace {
    grid-template-columns: 1fr;
  }

  .provisioning-detail-panel {
    max-height: none;
    order: -1;
    overflow: visible;
    position: static;
  }
}

@media (max-width: 767.98px) {
  .provisioning-action {
    flex: 1 1 calc(50% - 0.25rem);
    min-height: 64px;
    min-width: 0;
  }

  .provisioning-action i {
    font-size: 1.35rem;
  }

  .provisioning-table-wrapper {
    display: none;
  }

  .provisioning-mobile-list {
    display: grid;
    gap: 0.75rem;
    padding: 0 0.75rem 0.75rem;
  }

  .provisioning-request-card {
    background: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 0.5rem;
    color: inherit;
    padding: 0.85rem;
    text-align: left;
    width: 100%;
  }

  .provisioning-request-card.active {
    background: var(--bs-primary-bg-subtle);
    border-color: var(--bs-primary);
  }

  .provisioning-detail-panel {
    order: 0;
  }
}

@media (min-width: 992px) and (max-width: 1399.98px) {
  .provisioning-workspace {
    grid-template-columns: 1fr;
  }

  .provisioning-detail-panel {
    max-height: none;
    order: -1;
    overflow: visible;
    position: static;
  }
}
</style>
